// Workstream H (H4) — import a standard SKILL.md skill from a file, a URL, or
// a GitHub repo. The bundle format is the cross-vendor open standard, so a
// skill authored in Claude/Codex/Gemini — or Anthropic's public github skills —
// imports here. Imported skills land as reviewable DRAFTS (untrusted source);
// bundled scripts never auto-run.

import { useState } from "react";
import { COLORS } from "../../theme/colors";
import { skillsApi } from "../../api/client";

const BORDER = COLORS.border || "#e2e5eb";
const MUTED = COLORS.textMuted || "#6b7280";
const DIM = COLORS.textDim || "#9090a8";
const BLUE = COLORS.accent || "#3b82f6";

const SOURCES = [
  { id: "file", label: "File" },
  { id: "url", label: "URL" },
  { id: "git", label: "GitHub" },
];

function tabBtn(active) {
  return {
    font: "inherit", fontSize: 13, fontWeight: 600, padding: "7px 14px",
    cursor: "pointer", background: "transparent", border: 0,
    color: active ? COLORS.text : MUTED,
    borderBottom: active ? `2px solid ${BLUE}` : "2px solid transparent",
  };
}

const inputStyle = {
  width: "100%", font: "inherit", fontSize: 13, color: COLORS.text,
  background: COLORS.surface, border: `1px solid ${BORDER}`,
  borderRadius: 9, padding: "9px 11px", boxSizing: "border-box",
};

export default function ImportSkillModal({ onClose, onImported }) {
  const [source, setSource] = useState("file");
  const [file, setFile] = useState(null);
  const [url, setUrl] = useState("");
  const [gitRef, setGitRef] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit =
    (source === "file" && file) ||
    (source === "url" && url.trim()) ||
    (source === "git" && gitRef.trim());

  const submit = async () => {
    if (!canSubmit || busy) return;
    setBusy(true);
    setError(null);
    try {
      let res;
      if (source === "file") res = await skillsApi.importSkillFile(file);
      else if (source === "url") res = await skillsApi.importSkillUrl(url.trim());
      else res = await skillsApi.importSkillGit(gitRef.trim());
      const data = res && res.data ? res.data : res;
      // Pass the source filename (file imports only) so the caller can flag the
      // filename-vs-`name:` mismatch that trips people up.
      onImported && onImported(data, source === "file" && file ? file.name : null);
      onClose();
    } catch (e) {
      setError(
        (e && e.message) ||
          "Couldn't import that skill. Check the file/URL and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Import a skill"
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "grid", placeItems: "center", zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(520px, 92vw)", background: COLORS.surface,
          border: `1px solid ${BORDER}`, borderRadius: 16, padding: 20,
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
        }}
      >
        <h3 style={{ margin: "0 0 4px", fontSize: 17, color: COLORS.text }}>Import a skill</h3>
        <p style={{ margin: "0 0 14px", fontSize: 12.5, color: DIM, maxWidth: "60ch" }}>
          Bring in a standard <code>SKILL.md</code> bundle — from a file, a URL, or a
          GitHub repo (e.g. <code>anthropics/skills/document-skills/pdf</code>). It
          imports as a <b>draft</b> for you to review; bundled scripts never run
          automatically.
        </p>

        <div style={{ display: "flex", gap: 4, borderBottom: `1px solid ${BORDER}`, marginBottom: 14 }}>
          {SOURCES.map((s) => (
            <button key={s.id} type="button" style={tabBtn(source === s.id)}
              onClick={() => { setSource(s.id); setError(null); }}>
              {s.label}
            </button>
          ))}
        </div>

        {source === "file" && (
          <input
            aria-label="Skill bundle file"
            type="file"
            accept=".zip,.md"
            onChange={(e) => setFile(e.target.files && e.target.files[0])}
            style={{ ...inputStyle, padding: "8px 10px" }}
          />
        )}
        {source === "url" && (
          <input
            aria-label="Skill URL"
            type="url"
            placeholder="https://…/SKILL.md  or  https://…/skill.zip"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            style={inputStyle}
          />
        )}
        {source === "git" && (
          <input
            aria-label="GitHub reference"
            type="text"
            placeholder="anthropics/skills/document-skills/pdf"
            value={gitRef}
            onChange={(e) => setGitRef(e.target.value)}
            style={inputStyle}
          />
        )}

        {error && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: COLORS.red || "#ef4444" }}>{error}</div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
          <button type="button" onClick={onClose} style={{
            font: "inherit", fontSize: 13, fontWeight: 600, color: MUTED, cursor: "pointer",
            background: "transparent", border: `1px solid ${BORDER}`, padding: "8px 14px", borderRadius: 9,
          }}>Cancel</button>
          <button type="button" onClick={submit} disabled={!canSubmit || busy} style={{
            font: "inherit", fontSize: 13, fontWeight: 700, color: "#fff", cursor: canSubmit && !busy ? "pointer" : "default",
            background: BLUE, border: 0, padding: "8px 16px", borderRadius: 9, opacity: canSubmit && !busy ? 1 : 0.55,
          }}>{busy ? "Importing…" : "Import"}</button>
        </div>
      </div>
    </div>
  );
}
